import { useRef, useState } from "react";
import { toast } from "sonner";
import { FileDown, Loader2, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, formatApiError } from "@/lib/api";
import { downloadImportTemplate } from "@/lib/exporters";
import { ui } from "@/lib/ui";

export const CsvImport = ({ onImported }) => {
  const input = useRef(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) {
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/jobs/import", fd);
      setResult(data);
      toast.success(`Imported ${data.imported} job${data.imported === 1 ? "" : "s"}`, data.skipped ? { description: `${data.skipped} row(s) skipped` } : undefined);
      onImported();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-dashed border-cyan-500/30 bg-[#050A14] p-5 sm:p-6" data-testid="csv-import-panel">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <p className="font-display text-sm uppercase tracking-widest">Data ingestion</p>
          <p className="text-xs text-slate-500 mt-1 max-w-xl">Upload a CSV of past jobs or offline logs. Required columns: date, pickup, drop-off, fee. Exports from this dashboard can be re-imported.</p>
        </div>
        <div className="flex gap-2 shrink-0">
          <Button variant="ghost" onClick={downloadImportTemplate} className="rounded-none font-mono text-xs uppercase text-slate-400 hover:text-cyan-300 hover:bg-cyan-950/40" data-testid="download-import-template-button">
            <FileDown className="w-4 h-4 mr-1.5" />Template
          </Button>
          <Button onClick={() => input.current?.click()} disabled={busy} className={`${ui.btnSecondary} h-10`} data-testid="import-jobs-csv-button">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}Upload CSV
          </Button>
          <input ref={input} type="file" accept=".csv,text/csv" className="hidden" onChange={upload} data-testid="import-jobs-csv-input" />
        </div>
      </div>
      {result && (
        <div className="mt-4 font-mono text-xs" data-testid="csv-import-result">
          <p className="text-[#00FF66]">&gt; {result.imported} imported · {result.skipped} skipped</p>
          {result.errors.map((e) => <p key={e.row} className="text-[#FF6B00]">&gt; row {e.row}: {e.error}</p>)}
        </div>
      )}
    </div>
  );
};
